import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { BlurText } from './animations/BlurText';
import { ShinyText } from './animations/ShinyText';
const haveTools = ['Scanners', 'Cloud alerts', 'EDR', 'SIEM', 'Tickets', 'Dashboards', 'Compliance screenshots', 'Pentest PDFs'];
const questions = ['What can actually hurt us?', 'Which control failed?', 'Which path matters most?', 'What should we fix first?', 'Did the fix work?', 'Can we prove it?'];
export function Problem() {
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-1231">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1232">
        <SectionLabel number="02" label="The Problem" className="mb-8" data-id="element-1233" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1234">
          {/* Headline */}
          <div className="lg:col-span-12 mb-8" data-id="element-1235">
            <motion.h2 initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-1236">
              Your tools found a lot.
              <br data-id="element-1237" />
              <span className="text-white/40" data-id="element-1238">Now what?</span>
            </motion.h2>
            <p className="mt-6 text-lg text-white/65 leading-relaxed max-w-3xl" data-id="element-1239">
              Most teams already have scanners, cloud alerts, EDR, SIEM,
              tickets, dashboards, compliance screenshots, and pentest PDFs.
              Still, the important questions are unresolved.
            </p>
            <p className="mt-4 text-lg text-white leading-relaxed max-w-3xl" data-id="element-1240">
              Periscan gives security teams a continuous way to test what holds,
              what fails, and what needs fixing now.
            </p>
          </div>

          {/* Tools they have */}
          <div className="lg:col-span-5" data-id="element-1241">
            <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6" data-id="element-1242">
              What they already have
            </p>
            {/*
             The "stack manifest" — a numbered inventory readout rather
             than the default chip-cloud pattern. Reads like a procurement
             line item, which is exactly the framing: a list of stuff
             you've bought. The line at the bottom delivers the punchline
             silently.
             */}
            <div className="border-t border-b border-white/[0.08]" data-id="element-1243">
              {haveTools.map((tool, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -8
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: i * 0.04
            }} className="flex items-baseline gap-4 py-2.5 border-b border-white/[0.04] last:border-b-0" data-id="element-1244">
                  <span className="font-mono text-[10px] text-white/30 tabular-nums tracking-widest" data-id="element-1245">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="h-px flex-shrink-0 w-4 bg-white/10" data-id="element-1246" />
                  <span className="text-[15px] text-white/75 leading-snug" data-id="element-1247">
                    {tool}
                  </span>
                </motion.div>)}
            </div>
            <div className="mt-3 flex items-baseline justify-between gap-4 font-mono text-[10px] uppercase tracking-widest" data-id="element-1248">
              <span className="text-white/35" data-id="element-1249">
                {haveTools.length} tools installed
              </span>
              <span className="text-[#1a8fd4]" data-id="element-1250">0 questions answered</span>
            </div>
            <p className="mt-8 text-2xl text-white font-medium leading-snug" data-id="element-1251">
              But when the board, auditor, or customer asks,
              <br data-id="element-1252" />
              security teams still can't answer:
            </p>
            <p className="mt-4 text-3xl md:text-4xl text-[#1a8fd4] font-semibold tracking-tight leading-tight" data-id="element-1253">
              Are our defenses
              <br data-id="element-1254" />
              actually working right now?
            </p>
          </div>

          {/* Gaps */}
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/5" data-id="element-1255">
            <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6" data-id="element-1256">
              Where existing tools fall short
            </p>
            <div className="border-t border-b border-white/[0.08]" data-id="element-1257">
              {questions.map((question, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: 10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.08
            }} className="flex items-center gap-6 py-5 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] transition-colors px-4 -mx-4 rounded-lg" data-id="element-1258">
                  <span className="font-mono text-xs text-white/30 tabular-nums tracking-widest" data-id="element-1259">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="h-px flex-shrink-0 w-8 bg-white/10" data-id="element-1260" />
                  <span className="text-xl md:text-2xl font-medium text-white/90" data-id="element-1261">
                    {question}
                  </span>
                </motion.div>)}
            </div>
            <p className="mt-8 text-xl text-[#1a8fd4] font-medium leading-relaxed" data-id="element-1262">
              Periscan answers those questions directly.
            </p>
          </div>
        </div>
      </div>
    </section>;
}